import { extendTheme } from '@chakra-ui/react';

// Windfall "brasão e corrente" identity.
// Deriving the palette from the lion crest: navy institutional base,
// royal blue for actions, steel for the "current" motif, brass as the
// rare heraldic accent. The app currently uses default Chakra `blue.*`
// tokens everywhere, so remapping the `blue` scale here re-skins the
// whole shell (headers, sidebar, footer, buttons) with no per-file edits.
const brandBlue = {
	50: '#eef3fc',
	100: '#d3e0f7',
	200: '#aec6ef',
	300: '#7fa0dd',
	400: '#4b74cf',
	500: '#1E56C8', // royal - primary action / active state
	600: '#1a49ab',
	700: '#16305A', // navy - headers, sidebar, footer
	800: '#10264a',
	900: '#0E1F38' // ink - deepest surface / text
};

const brass = {
	50: '#faf3e2',
	100: '#f0dcac',
	200: '#e4c47a',
	300: '#d5ac52',
	400: '#cca049',
	500: '#C69A44', // heraldic accent, used with restraint
	600: '#a97f33',
	700: '#856226',
	800: '#5f451a',
	900: '#3a2a0e'
};

const theme = extendTheme({
	fonts: {
		heading: `'Oswald', 'Poppins', system-ui, sans-serif`,
		body: `'Poppins', system-ui, sans-serif`
	},
	colors: {
		blue: brandBlue,
		brand: brandBlue,
		brass,
		steel: '#7C99C4',
		paper: '#F4F6FA',
		mist: '#E4EAF3',
		ink: '#0E1F38'
	},
	radii: {
		md: '10px',
		lg: '14px',
		xl: '18px'
	},
	shadows: {
		// visible keyboard focus ring in the brand royal, applied to every
		// focusable Chakra component via the `outline` shadow token
		outline: '0 0 0 3px rgba(30, 86, 200, 0.5)'
	},
	styles: {
		global: {
			body: {
				bg: '#F4F6FA',
				color: '#0E1F38'
			}
		}
	},
	components: {
		Button: {
			baseStyle: {
				fontWeight: 700,
				letterSpacing: '0.01em',
				borderRadius: '10px'
			}
		},
		Input: {
			defaultProps: {
				focusBorderColor: 'blue.500'
			}
		},
		Select: {
			defaultProps: {
				focusBorderColor: 'blue.500'
			}
		},
		Badge: {
			baseStyle: {
				borderRadius: '6px',
				textTransform: 'none',
				fontWeight: 700,
				px: 2,
				py: '2px'
			}
		},
		Heading: {
			baseStyle: {
				letterSpacing: '-0.01em'
			}
		},
		FormLabel: {
			baseStyle: {
				fontSize: '14px',
				fontWeight: 600,
				color: 'gray.600',
				mb: '6px'
			}
		},
		Table: {
			baseStyle: {
				th: {
					borderColor: 'mist',
					color: 'gray.500',
					letterSpacing: '0.04em'
				},
				td: {
					borderColor: 'mist'
				},
				caption: {
					color: 'gray.400'
				}
			}
		},
		Switch: {
			defaultProps: {
				colorScheme: 'green'
			}
		},
		Drawer: {
			baseStyle: {
				header: {
					borderBottomColor: 'mist',
					fontFamily: 'heading',
					textTransform: 'uppercase',
					letterSpacing: '0.01em'
				},
				footer: {
					borderTopColor: 'mist'
				}
			}
		},
		Modal: {
			baseStyle: {
				dialog: {
					borderRadius: '16px'
				},
				header: {
					fontFamily: 'heading',
					textTransform: 'uppercase',
					letterSpacing: '0.01em'
				}
			}
		},
		Menu: {
			baseStyle: {
				list: {
					borderColor: 'mist',
					borderRadius: '12px',
					boxShadow: 'lg'
				},
				item: {
					_hover: { bg: 'blue.50' },
					_focus: { bg: 'blue.50' }
				}
			}
		},
		Card: {
			baseStyle: {
				container: {
					borderRadius: '14px',
					borderColor: 'mist'
				}
			}
		}
	}
});

export default theme;
